import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  Link,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';

import { formatCurrency, formatDateOnly } from '../utils/formatting';
import { RESTRICTED_VENDORS } from '../utils/constants';

Font.register({
  family: 'Inter',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuGKYMZg.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuFuYMZg.ttf', fontWeight: 700 },
  ],
});

Font.register({
  family: 'Open Sans',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/opensans/v44/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0C4n.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/opensans/v44/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsgH1y4n.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/opensans/v44/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsg-1y4n.ttf', fontWeight: 700 },
  ],
});

/*
 * DraftOrderDetailPDF
 * React-PDF version of the WillyGoat quote PDF. Mirrors the layout/styling of
 * the Mechanic Liquid task (_other_files/_mechanic/draft-pdf.liquid).
 *
 * Data comes from the same shape used by DraftOrderDetailPage (`draftOrder`).
 * Anything not present in that data is rendered as an ALL CAPS placeholder.
 */

// ---------------------------------------------------------------------------
// Constants / assets
// ---------------------------------------------------------------------------
const LOGO_URL =
  'https://willygoat.com/cdn/shop/files/willygoat-logo-outlined-head-mobile0_270x@2x.png?v=1650395508';
const BADGE_URL =
  'https://cdn.shopify.com/s/files/1/0100/6072/6369/files/Proven-Process.png?v=1767810165';

const COLORS = {
  green: '#317853',
  lightGrey: '#9a9c9d',
  darkGrey: '#434547',
  border: '#d5d7d9',
};

const COMPANY = {
  poBox: 'PO BOX 59278',
  cityState: 'BIRMINGHAM, AL 35259',
  phone: '888.920.4628',
  legalName: 'WILLYGOAT, LLC',
  payableAddress: 'PO Box 59278 Birmingham, AL 35259',
};

// Attributes that should never render in the line-item description.
const HIDDEN_ATTR_KEYS = ['_leadtime', 'installation manual', 'packing list'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const money = (amount, currency) =>
  formatCurrency(amount == null || amount === '' ? 0 : amount, currency || 'USD');

const isRestricted = (item) =>
  RESTRICTED_VENDORS.includes(item.vendor) ||
  (item.variantTitle || '').includes('Installation') || (item.title || '').includes('Shade Structure Engineered Drawings') || (item.title || '').includes('Crating Fee');

const formatAttrKey = (key) =>
  key.replace('_LeadTime', 'Lead Time').replace(/_/g, ' ').trim();

const formatAttrValue = (value) => String(value).replace(/_/g, ' ').trim();

const showVariantTitle = (item) => {
  const vt = item.variantTitle;
  if (!vt || vt === 'Default Title') return false;
  return !isRestricted(item);
};

/** Per-line taxable label. `taxable` isn't in the portal data shape, so fall back to a placeholder. */
const taxableLabel = (item, taxExempt) => {
  if (taxExempt) return 'No';
  if (typeof item.taxable === 'boolean') return item.taxable ? 'Yes' : 'No';
  return 'Yes';
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 42,
    fontFamily: 'Open Sans',
    fontSize: 8,
    color: COLORS.darkGrey,
    lineHeight: 1.15,
  },

  // Header
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: { width: 165 },
  headerRight: { alignItems: 'flex-end' },
  quoteTitle: {
    fontFamily: 'Inter',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: 13,
    color: COLORS.darkGrey,
    marginBottom: 1,
    lineHeight: 1.2,
  },
  headerMeta: { fontSize: 8, color: COLORS.lightGrey, textAlign: 'right' },

  companyInfo: { marginTop: 2 },
  companyLine: { fontSize: 8, lineHeight: 1.15 },

  // Addresses
  addresses: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: 10,
    marginBottom: 6,
  },
  address: { minWidth: 150, maxWidth: 200 },
  addressHeading: {
    fontFamily: 'Inter',
    fontWeight: 'bold',
    fontSize: 9,
    color: COLORS.darkGrey,
    textTransform: 'uppercase',
    marginBottom: 1,
  },
  addressLine: { fontSize: 8, lineHeight: 1.15, textTransform: 'capitalize', },
  badge: { width: 105, marginTop: -26, justifySelf: 'flex-end', },

  // Line items table
  table: { marginTop: 12 },
  theadRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 4,
  },
  th: {
    fontFamily: 'Inter',
    fontWeight: 'bold',
    fontSize: 8,
    textTransform: 'uppercase',
    color: COLORS.darkGrey,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingTop: 4,
    paddingBottom: 4,
  },
  td: { fontSize: 8, color: COLORS.darkGrey },

  // Column widths
  colQty: { width: '6%', textAlign: 'center' },
  colDesc: { width: '43%', paddingRight: 6 },
  colSku: { width: '15%', textAlign: 'center', textTransform: 'uppercase' },
  colUnit: { width: '14%', textAlign: 'center' },
  colTax: { width: '9%', textAlign: 'center', textTransform: 'uppercase' },
  colAmt: { width: '13%', textAlign: 'right' },

  itemTitle: { 
    fontFamily: 'Open Sans', 
    fontSize: 8, 
    color: COLORS.darkGrey,
    fontWeight: 'bold', 
    lineHeight: 1.2,
  },
  itemOption: {
    fontSize: 7.5,
    color: COLORS.darkGrey,
    textTransform: 'capitalize',
    lineHeight: 1.2,
    // marginTop: 1,
  },

  // Totals
  totalsWrap: { flexDirection: 'row', marginTop: 6 },
  totalsSpacer: { width: '52%' },
  totals: { width: '48%' },
  totalLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 1.1,
  },
  totalLabel: { fontSize: 8, color: COLORS.darkGrey },
  totalValue: { fontFamily: 'Open Sans', 
    fontSize: 8, 
    textAlign: 'right',
    fontWeight: 'bold'
  },
  payableBox: {
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
  },
  payableText: { fontSize: 8, lineHeight: 1.2 },

  // Review / contact
  reviewBlock: { marginTop: 12 },
  reviewText: { fontSize: 7.5, marginBottom: 4, lineHeight: 1.2 },
  contactRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  link: { color: COLORS.green, textDecoration: 'underline' },

  hr: { borderTopWidth: 1, borderTopColor: COLORS.border, marginTop: 12 },

  // Disclaimer
  disclaimer: { marginTop: 8 },
  disclaimerText: { fontSize: 7, lineHeight: 1.2, marginTop: 2, marginBottom: 2, color: COLORS.darkGrey },
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function AddressBlock({ heading, address }) {
  if (!address) return null;
  const cityLine = [address.city, address.province].filter(Boolean).join(', ');
  const cityZip = [cityLine, address.zip].filter(Boolean).join(' ');
  return (
    <View style={styles.address}>
      <Text style={styles.addressHeading}>{heading}</Text>
      {address.name ? <Text style={styles.addressLine}>{address.name}</Text> : null}
      {address.company ? <Text style={styles.addressLine}>{address.company}</Text> : null}
      {address.address1 ? (
        <Text style={styles.addressLine}>
          {address.address1}
          {address.address2 ? ` ${address.address2}` : ''}
        </Text>
      ) : null}
      {cityZip ? <Text style={styles.addressLine}>{cityZip}</Text> : null}
      {address.phone ? <Text style={styles.addressLine}>{address.phone}</Text> : null}
    </View>
  );
}

function LineItemRow({ item, currency, taxExempt }) {
  const unit = money(item.price, currency);
  const amount = money(Number(item.price || 0) * Number(item.quantity || 0), currency);
  const sku = isRestricted(item) ? '' : item.sku || '';

  const options = (item.customAttributes || []).filter(
    (attr) => !HIDDEN_ATTR_KEYS.includes(String(attr.key).toLowerCase())
  );

  return (
    <View style={styles.row} wrap={false}>
      <Text style={[styles.td, styles.colQty]}>{item.quantity}</Text>

      <View style={styles.colDesc}>
        <Text>
          <Text style={styles.itemTitle}>{item.title}</Text>
          {showVariantTitle(item) ? (
            <Text style={styles.itemTitle}> - {item.variantTitle}</Text>
          ) : null}
        </Text>
        {options.map((attr, idx) => (
          <Text key={idx} style={styles.itemOption}>
            {formatAttrKey(attr.key)}: {formatAttrValue(attr.value)}
          </Text>
        ))}
      </View>

      <Text style={[styles.td, styles.colSku]}>{sku}</Text>
      <Text style={[styles.td, styles.colUnit]}>{unit}</Text>
      <Text style={[styles.td, styles.colTax]}>{taxableLabel(item, taxExempt)}</Text>
      <Text style={[styles.td, styles.colAmt]}>{amount}</Text>
    </View>
  );
}

function TotalsBlock({ draftOrder }) {
  const currency = draftOrder.currency || 'USD';
  const discount = Number(draftOrder.appliedDiscount || 0);
  // Match the Liquid: subtotal shown is pre-discount (subtotal + discount added back).
  const subtotalDisplay = Number(draftOrder.lineItemsSubtotalPrice || (draftOrder.subtotalPrice + discount) || 0);
  const shipping = draftOrder.totalShippingPrice2;
  const discountTitle = (draftOrder.appliedDiscountTitle || 'Discount').toUpperCase();

  return (
    <View style={styles.totalsWrap}>
      <View style={styles.totalsSpacer} />
      <View style={styles.totals}>
        <View style={styles.totalLine}>
          <Text style={styles.totalLabel}>Subtotal</Text>
          <Text style={styles.totalValue}>{money(subtotalDisplay, currency)}</Text>
        </View>

        <View style={styles.totalLine}>
          <Text style={styles.totalLabel}>Shipping</Text>
          <Text style={styles.totalValue}>{money(shipping, currency)}</Text>
        </View>

        {draftOrder.taxExempt ? (
          <View style={styles.totalLine}>
            <Text style={styles.totalLabel}>Tax Exempt</Text>
            <Text style={styles.totalValue} />
          </View>
        ) : (
          (draftOrder.totalTax ? (
            <View style={styles.totalLine}>
              <Text style={styles.totalLabel}>Tax</Text>
              <Text style={styles.totalValue}>{money(draftOrder.totalTax, currency)}</Text>
            </View>
          ) : null)
          // (draftOrder.taxLines || []).map((line, idx) => {
          //   const pct =
          //     line.ratePercentage != null
          //       ? line.ratePercentage
          //       : line.rate != null
          //       ? Number(line.rate) * 100
          //       : null;
          //   return (
          //     <View style={styles.totalLine} key={idx}>
          //       <Text style={styles.totalLabel}>
          //         ({line.title}
          //         {pct != null ? ` ${pct}%` : ''})
          //       </Text>
          //       <Text style={styles.totalValue}>{money(line.price, currency)}</Text>
          //     </View>
          //   );
          // })
        )}

        {discount > 0 ? (
          <View style={styles.totalLine}>
            <Text style={styles.totalLabel}>Discount ({discountTitle})</Text>
            <Text style={styles.totalValue}>-{money(discount, currency)}</Text>
          </View>
        ) : null}

        <View style={styles.totalLine}>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalValue}>{money(draftOrder.totalPrice, currency)}</Text>
        </View>

        <View style={styles.payableBox}>
          <Text style={styles.payableText}>Make all checks payable to:</Text>
          <Text style={[styles.payableText, { fontFamily: 'Open Sans', fontWeight: 'bold' }]}>
            {COMPANY.legalName}
          </Text>
          <Text style={styles.payableText}>{COMPANY.payableAddress}</Text>
        </View>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Main document
// ---------------------------------------------------------------------------
function DraftOrderDetailPDF({ draftOrder, shop = {} }) {
  if (!draftOrder) return null;

  const currency = draftOrder.currency || 'USD';
  const quoteName = draftOrder.name || draftOrder.order?.name || 'QUOTE NUMBER';
  const supportEmail = 'fun@willygoat.com';
  const lineItems = draftOrder.lineItems || [];

  return (
    <Document title={`WillyGoat Quote ${quoteName}`}>
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.headerRow}>
          <Image style={styles.logo} src={LOGO_URL} />
          <View style={styles.headerRight}>
            <Text style={styles.quoteTitle}>Quote {quoteName}</Text>
            {draftOrder.invoiceSentAt ? (
              <Text style={styles.headerMeta}>
                Date: {formatDateOnly(draftOrder.invoiceSentAt)}
              </Text>
            ) : null}
            {draftOrder.expirationDate ? (
              <Text style={styles.headerMeta}>
                Quote valid until: {formatDateOnly(draftOrder.expirationDate)}
              </Text>
            ) : null}
          </View>
        </View>

        <View style={styles.companyInfo}>
          <Text style={styles.companyLine}>{COMPANY.poBox}</Text>
          <Text style={styles.companyLine}>{COMPANY.cityState}</Text>
          <Text style={styles.companyLine}>{COMPANY.phone}</Text>
        </View>

        {/* Addresses */}
        <View style={styles.addresses}>
          <AddressBlock heading="Shipping Address:" address={draftOrder.shippingAddress} />
          {draftOrder.billingAddress ? (
            <AddressBlock heading="Billing Address:" address={draftOrder.billingAddress} />
          ) : null}
          <Image style={styles.badge} src={BADGE_URL} />
        </View>

        {/* Line items */}
        <View style={styles.table}>
          <View style={styles.theadRow}>
            <Text style={[styles.th, styles.colQty]}>Qty</Text>
            <Text style={[styles.th, styles.colDesc]}>Description</Text>
            <Text style={[styles.th, styles.colSku]}>SKU</Text>
            <Text style={[styles.th, styles.colUnit]}>Unit Price</Text>
            <Text style={[styles.th, styles.colTax]}>Taxable</Text>
            <Text style={[styles.th, styles.colAmt]}>Amount</Text>
          </View>

          {lineItems.map((item, idx) => (
            <LineItemRow
              key={idx}
              item={item}
              currency={currency}
              taxExempt={draftOrder.taxExempt}
            />
          ))}
        </View>

        {/* Totals */}
        <TotalsBlock draftOrder={draftOrder} />

        {/* Review / contact */}
        <View style={styles.reviewBlock}>
          <Text style={styles.reviewText}>
            Please review this quote carefully to ensure it completely and accurately
            reflects your project needs, provided measurements, and intended installation
            methods. Approval of this document indicates that you have verified all
            equipment and surfacing quantities are correct for your specific application.
          </Text>
          <Text style={styles.reviewText}>
            For any questions, <Link style={styles.link} src={`mailto:${supportEmail}`}>please contact us</Link>!
          </Text>
          <View style={styles.contactRow}>
            <Text>
              Phone: <Link style={styles.link} src={`tel:${COMPANY.phone}`}>{COMPANY.phone}</Link>
            </Text>
            <Text>THANK YOU FOR YOUR BUSINESS!</Text>
            <Text>
              Email: <Link style={styles.link} src={`mailto:${supportEmail}`}>{supportEmail}</Link>
            </Text>
          </View>
        </View>

        <View style={styles.hr} />

        {/* Disclaimer */}
        <View style={styles.disclaimer}>
          <Text style={styles.disclaimerText}>
            Confirm Your Order: Orders must be confirmed via phone at {COMPANY.phone} or by
            replying to this email. Payment must be received by the quote expiration date.
          </Text>
          <Text style={styles.disclaimerText}>
            Review Terms & Policies: By ordering, you accept WillyGoat's Terms & Policies.
            Review our Shipping Policy <Link style={styles.link} src="https://willygoat.com/policies/shipping-policy">(https://willygoat.com/policies/shipping-policy)</Link> &
            Refund Policy <Link style={styles.link} src="https://willygoat.com/policies/refund-policy">(https://willygoat.com/policies/refund-policy)</Link> before placing an
            order.
          </Text>
          <Text style={styles.disclaimerText}>
            Lead Times & Payment: Lead times are subject to change. Orders paid by credit
            card void discounts. Payment address: WillyGoat LLC, PO Box 59278, Birmingham,
            AL 35259. Please request account information if pay via ACH or wire transfer.
          </Text>
          <Text style={styles.disclaimerText}>
            Installation Requirements: If installation is included, please review site prep,
            unforeseen conditions, delivery responsibilities, and permit requirements Terms
            of Service <Link style={styles.link} src="https://willygoat.com/policies/terms-of-service">(https://willygoat.com/policies/terms-of-service)</Link>.
          </Text>
          <Text style={styles.disclaimerText}>
            All disputes arising out of payment for goods and services will be handled in
            the jurisdiction of Jefferson County, Alabama.
          </Text>
        </View>
      </Page>
    </Document>
  );
}

export default DraftOrderDetailPDF;
